import React, { useState, useContext, useRef, useEffect, useMemo } from "react";
import cx from "classnames";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { Heading, SkeletonBase, Text } from "@foris/avocado-suite";
import { Icon } from "@foris/avocado-icons";
import { Breadcrumbs } from "@foris/avocado-suite";
import Details from "./Details/Details";
import { IInfoHeader } from "./IHeaderModule";
import { IContextApp, ContextApp } from "@config/Context/contextApp";
import { ContextEDH } from "@context/ContextEDH";
import * as mouseflow from "@utils/mouseflow";
import SectionMenu from "./SectionMenu";
import css from "./header.module.scss";
import { Link } from "react-router-dom";

const Header: React.FC<IInfoHeader> = (props: IInfoHeader) => {
  const {
    className = "",
    titleClassName = "",
    type,
    topTitle,
    title,
    subTitle,
    canEditCourse,
    children,
    moreDetails,
    setHeight,
    dimension,
    renderMenu,
    hasSessions,
    onShowError,
    breadcrumbItems,
    shouldRenderSectionMenu,
    isLoading = {},
  } = props;

  const [openInfo, setOpenInfo] = useState(false);
  const [showDivision, setShowDivision] = useState(false);

  const context: IContextApp = useContext(ContextApp);
  const { state } = useContext(ContextEDH);
  const { t } = useTranslation();

  const userCanEdit =
    context?.user?.permissions?.update &&
    context?.user?.abilities?.can_edit_links_and_sections &&
    type === "Sections" &&
    canEditCourse &&
    shouldRenderSectionMenu;

  const isStringTopTitle = typeof topTitle === "string";
  const isStringTitle = typeof title === "string";
  const headerDimension = useRef(null);

  useEffect(() => {
    if (headerDimension !== null && headerDimension.current && setHeight) {
      setHeight(headerDimension.current.offsetHeight);
    }
  }, []);

  useEffect(() => {
    const hasLoading = Object.keys(isLoading)?.some(key => isLoading[key]);

    if (hasLoading) {
      openInfo && setOpenInfo(false);
      showDivision && setShowDivision(false);
    }
  }, [isLoading]);

  const adaptedBreadcrumbItems = useMemo(() => {
    return (breadcrumbItems ?? []).map(item =>
      item?.path
        ? {
            ...item,
            component: <Link to={item?.path} />,
          }
        : item,
    );
  }, [breadcrumbItems]);

  return (
    <header
      className={cx(
        css.cntHeader,
        moreDetails && moreDetails.length > 0 && css.cntHeader__moreInfo,
        "container-row row--between",
        className,
      )}
      ref={headerDimension}
    >
      {(adaptedBreadcrumbItems?.length > 0 || isLoading?.breadcrumb) && (
        <div className={css.cntHeader_breadcrumbContent}>
          {isLoading?.breadcrumb ? (
            <SkeletonBase width={90} height={14} />
          ) : (
            <Breadcrumbs darkMode items={adaptedBreadcrumbItems} />
          )}
        </div>
      )}

      <div className={cx(css.cntHeader_content, showDivision && css.cntHeader_content__divided)}>
        {dimension || isLoading?.dimension ? (
          <div className={cx(css.dimensionWrapper, "avocado-light-theme")}>
            <Text className={css.dimensionWrapper_title}>
              {isLoading?.dimension ? (
                <SkeletonBase width={66} height={16} />
              ) : (
                t("header.dimension.title")
              )}
            </Text>
            <Heading className={css.dimensionWrapper_name} type="h1">
              {isLoading?.dimension ? <SkeletonBase width={85} height={30} /> : dimension}
            </Heading>
          </div>
        ) : null}

        <section className={css.infoItem}>
          {isLoading?.topTitle ? (
            <SkeletonBase className={css.infoItem_topTitle} width={235} height={16} />
          ) : isStringTopTitle ? (
            <Text darkMode className={css.infoItem_topTitle}>
              {topTitle}
            </Text>
          ) : (
            <>{topTitle}</>
          )}

          {isLoading?.title ? (
            <SkeletonBase
              className={cx(css.infoItem_title, titleClassName)}
              width={650}
              height={30}
            />
          ) : isStringTitle ? (
            <Heading type="h1" darkMode className={cx(css.infoItem_title, titleClassName)}>
              {title}
            </Heading>
          ) : (
            <>{title}</>
          )}

          {isLoading?.subTitle ? (
            <SkeletonBase className={css.infoItem_container} width={230} height={16} />
          ) : (
            subTitle && <div className={css.infoItem_container}>{subTitle}</div>
          )}
        </section>

        {userCanEdit || renderMenu ? (
          <div className={css.menuWrapper}>
            {renderMenu ? renderMenu() : null}

            {userCanEdit ? (
              <SectionMenu
                hasSessions={hasSessions}
                openEdit={() => props.callbackOpenEdit(true)}
                openDisabledSession={() => props.callbackOpenDisabledSession(true)}
                openChangeComponent={() => props.callbackOpenChangeComponent(true)}
                onDisabledSessionError={() => onShowError?.()}
              />
            ) : null}
          </div>
        ) : null}
      </div>

      <motion.div
        layout
        style={{
          overflow: "hidden",
          width: "100%",
        }}
        initial={{
          height: 0,
        }}
        animate={{
          height: openInfo ? "auto" : 0,
        }}
        transition={{
          duration: 0.3,
          ease: "easeInOut",
        }}
        onAnimationComplete={() => !openInfo && showDivision && setShowDivision(false)}
      >
        <div className={css.detailsWrapper}>
          <Details data={moreDetails} active>
            {children}
          </Details>
        </div>
      </motion.div>

      {(moreDetails && moreDetails.length > 0) || children || isLoading?.moreDetails ? (
        <div className={cx(css.linkTo, "container-row row--center")}>
          <button
            onClick={() => {
              if (isLoading?.moreDetails) return;

              const newValue = !openInfo;

              newValue &&
                mouseflow.actionTag("action_open_header", state?.base?.isMouseflowEnabled);

              setOpenInfo(newValue);
              !!newValue && setShowDivision(true);
            }}
            className={cx(css.linkTo_btn, openInfo && css.linkTo_btn__active)}
          >
            <Icon name="chevron-down" size="md" className={css.icon} />
            <Heading type="h4" className={css.title}>
              {openInfo
                ? t("header.information-additional.less")
                : t("header.information-additional.more")}
              {t("header.information-additional.title")}
            </Heading>
          </button>
        </div>
      ) : null}
    </header>
  );
};

export default Header;
